export interface RecordPdfRow {
  name: string;
  dob: string;
  idNumber: string;
  telephone1: string;
  telephone2: string;
  telephone3: string;
  telephone4: string;
  telephone5: string;
  telephone6: string;
  telephone7: string;
  telephone8: string;
}

function sanitizeCell(value: string): string {
  return value && value.trim() ? value.trim() : 'N/A';
}

export async function exportRecordsPdf(
  title: string,
  rows: RecordPdfRow[],
  filename: string
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a2' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const marginTop = 44;
  const rowHeight = 24;

  const columns = [
    { key: 'name', title: 'Name', width: 220 },
    { key: 'dob', title: 'DOB', width: 95 },
    { key: 'idNumber', title: 'ID Nr', width: 120 },
    { key: 'telephone1', title: 'Telephone 1', width: 110 },
    { key: 'telephone2', title: 'Telephone 2', width: 110 },
    { key: 'telephone3', title: 'Telephone 3', width: 110 },
    { key: 'telephone4', title: 'Telephone 4', width: 110 },
    { key: 'telephone5', title: 'Telephone 5', width: 110 },
    { key: 'telephone6', title: 'Telephone 6', width: 110 },
    { key: 'telephone7', title: 'Telephone 7', width: 110 },
    { key: 'telephone8', title: 'Telephone 8', width: 110 },
  ] as const;

  let y = marginTop;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, marginX, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString('en-GB', { timeZone: 'Africa/Johannesburg' })}`, marginX, y);
  y += 20;

  const drawHeader = () => {
    doc.setFillColor(236, 240, 245);
    doc.rect(marginX, y, pageWidth - marginX * 2, rowHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    let x = marginX + 6;
    for (const col of columns) {
      doc.text(col.title, x, y + 16);
      x += col.width;
    }
    y += rowHeight;
  };

  const drawRow = (row: RecordPdfRow) => {
    if (y + rowHeight > pageHeight - 40) {
      doc.addPage();
      y = marginTop;
      drawHeader();
    }
    doc.setDrawColor(220, 226, 235);
    doc.line(marginX, y, pageWidth - marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const values = {
      name: sanitizeCell(row.name),
      dob: sanitizeCell(row.dob),
      idNumber: sanitizeCell(row.idNumber),
      telephone1: sanitizeCell(row.telephone1),
      telephone2: sanitizeCell(row.telephone2),
      telephone3: sanitizeCell(row.telephone3),
      telephone4: sanitizeCell(row.telephone4),
      telephone5: sanitizeCell(row.telephone5),
      telephone6: sanitizeCell(row.telephone6),
      telephone7: sanitizeCell(row.telephone7),
      telephone8: sanitizeCell(row.telephone8),
    };
    let x = marginX + 6;
    for (const col of columns) {
      const text = values[col.key] as string;
      const clipped = doc.splitTextToSize(text, col.width - 12)[0] || '';
      doc.text(clipped, x, y + 16);
      x += col.width;
    }
    y += rowHeight;
  };

  drawHeader();
  if (!rows.length) {
    drawRow({
      name: 'N/A',
      dob: 'N/A',
      idNumber: 'N/A',
      telephone1: 'N/A',
      telephone2: 'N/A',
      telephone3: 'N/A',
      telephone4: 'N/A',
      telephone5: 'N/A',
      telephone6: 'N/A',
      telephone7: 'N/A',
      telephone8: 'N/A',
    });
  } else {
    rows.forEach(drawRow);
  }

  doc.save(filename);
}
